import { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { LayoutGrid, Phone, HeartPulse, Shield } from 'lucide-react'
import { useAuth } from '@/features/auth/AuthContext'

/**
 * eCare suite app switcher.
 *
 * Self-contained (DUPLICATED — do not abstract): see the equivalent file in
 * the ecarevoiceai voice-portal. Keep them in sync by hand.
 *
 * Entitlement model: role on the current Vitals session.
 *   - OPERATION_MANAGER / NURSE / SUPER_ADMIN / THINKADMIN → voice-access
 *   - Any authenticated user → vitals-access (they're already here)
 */

const CURRENT_APP_ID = 'vitals'

// Voice portal base URL (no slug) — fallback when tenant slug is unknown.
// Override via VITE_VOICE_URL in .env.production.
const VOICE_URL: string =
  (import.meta.env.VITE_VOICE_URL as string | undefined) ?? 'http://localhost:5190'

// Base domain for the Voice portal (e.g. "dev.ecarevoice.ai").
// When set, the AppSwitcher constructs https://{slug}.{VOICE_BASE_DOMAIN}
// so each tenant lands on their own subdomain.
const VOICE_BASE_DOMAIN: string | undefined =
  (import.meta.env.VITE_VOICE_BASE_DOMAIN as string | undefined) || undefined

// Use the absolute API base so this works in production (S3/CloudFront frontend
// can't proxy /api/* — Jenkins sets VITE_API_BASE_URL to the full backend URL).
const _API_BASE = ((import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api').replace(/\/$/, '')

type HandoffResult =
  | { ok: true; url: string }
  | { ok: false; reason: 'no_kc_session' | 'error' }

async function buildVoiceHandoffUrl(baseUrl: string): Promise<HandoffResult> {
  try {
    // Ask the Vitals backend to refresh the stored KC token and wrap it in a
    // short-lived signed code — raw KC tokens never appear in the URL.
    const resp = await fetch(`${_API_BASE}/auth/create-handoff-code`, {
      credentials: 'include',
    })
    if (resp.status === 404 || resp.status === 401 || resp.status === 502) {
      // 404 = no KC session stored, 401 = not logged in, 502 = KC refresh failed
      // All mean "no valid KC session" → show tooltip, don't navigate blind
      return { ok: false, reason: 'no_kc_session' }
    }
    if (!resp.ok) return { ok: false, reason: 'error' }

    const data = await resp.json() as { code?: string }
    if (!data.code) return { ok: false, reason: 'no_kc_session' }

    return { ok: true, url: `${baseUrl}/#code=${encodeURIComponent(data.code)}` }
  } catch {
    return { ok: false, reason: 'error' }
  }
}

const ADMIN_URL: string =
  (import.meta.env.VITE_ADMIN_URL as string | undefined) ?? 'http://localhost:5180'

const VITALS_URL =
  typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5300'

const APPS = [
  {
    id: 'voice',
    name: 'eCareVoice',
    url: VOICE_URL,
    adminOnly: false,
    icon: Phone,
    iconColor: '#3b82f6',
  },
  {
    id: 'vitals',
    name: 'eCareVitalsQ',
    url: VITALS_URL,
    adminOnly: false,
    icon: HeartPulse,
    iconColor: '#22c55e',
  },
  {
    id: 'admin',
    name: 'Admin Portal',
    url: ADMIN_URL,
    adminOnly: true,
    icon: Shield,
    iconColor: '#7c3aed',
  },
] as const

export function AppSwitcher(): React.JSX.Element {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const { user } = useAuth()

  // Build the tenant-specific Voice URL using slug subdomain when available.
  // e.g. https://thinkorg1.dev.ecarevoice.ai — falls back to VOICE_URL.
  const tenantVoiceUrl = useMemo(() => {
    const slug = user?.tenant_slug
    if (slug && VOICE_BASE_DOMAIN) return `https://${slug}.${VOICE_BASE_DOMAIN}`
    return VOICE_URL
  }, [user])

  const handleVoiceClick = useCallback(() => {
    setOpen(false)
    void buildVoiceHandoffUrl(tenantVoiceUrl).then((result) => {
      if (result.ok) {
        // Seamless SSO — handoff code carries the KC session.
        window.location.href = result.url
      } else {
        // No KC session or error — navigate to Voice anyway (user logs in there).
        window.location.href = tenantVoiceUrl
      }
    })
  }, [tenantVoiceUrl])

  const handleAdminClick = useCallback(() => {
    setOpen(false)
    void buildVoiceHandoffUrl(ADMIN_URL).then((result) => {
      window.location.href = result.ok ? result.url : ADMIN_URL
    })
  }, [])

  const visibleApps = useMemo(() => {
    if (!user) return APPS.filter((a) => a.id === CURRENT_APP_ID)
    const hasVoiceAccess = ['THINKADMIN', 'SUPER_ADMIN', 'OPERATION_MANAGER', 'NURSE', 'SALES_DIRECTOR'].includes(user.role)
    const hasAdminAccess = ['THINKADMIN', 'SUPER_ADMIN'].includes(user.role)
    return APPS.filter((a) => {
      if (a.id === CURRENT_APP_ID) return true
      if (a.id === 'voice') return hasVoiceAccess
      if (a.adminOnly) return hasAdminAccess
      return false
    })
  }, [user])

  useEffect(() => {
    if (!open) return undefined
    function onClick(e: MouseEvent): void {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div style={{ position: 'relative' }} ref={containerRef}>

      <button
        type="button"
        aria-label="Apps"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 32,
          height: 32,
          borderRadius: 6,
          border: 'none',
          background: open ? 'rgba(255,255,255,0.12)' : 'transparent',
          color: 'rgba(255,255,255,0.85)',
          cursor: 'pointer',
          transition: 'background 0.15s',
        }}
        onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.12)' }}
        onMouseLeave={(e) => { if (!open) (e.currentTarget as HTMLButtonElement).style.background = 'transparent' }}
      >
        <LayoutGrid size={16} strokeWidth={2} />
      </button>

      {open && (
        <div
          role="menu"
          aria-label="App switcher"
          style={{
            position: 'absolute',
            right: 0,
            top: 'calc(100% + 8px)',
            zIndex: 1000,
            width: 256,
            background: 'var(--color-surface, #fff)',
            border: '1px solid var(--color-border, #e5e7eb)',
            borderRadius: 12,
            boxShadow: '0 8px 32px rgba(0,0,0,0.14)',
            overflow: 'hidden',
            padding: '10px 10px',
          }}
        >
          <p style={{
            fontSize: 9.5,
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: 'var(--color-text-muted, #9ca3af)',
            margin: '0 0 8px 2px',
          }}>
            Applications
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4 }}>
            {visibleApps.map((app) => {
              const isCurrent = app.id === CURRENT_APP_ID
              const Icon = app.icon

              const tile = (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 6,
                  borderRadius: 8,
                  padding: '10px 4px',
                  textAlign: 'center',
                  background: isCurrent ? 'rgba(var(--color-accent-rgb, 59,130,246),0.08)' : 'transparent',
                  transition: 'background 0.15s',
                  cursor: isCurrent ? 'default' : 'pointer',
                }}
                onMouseEnter={e => { if (!isCurrent) (e.currentTarget as HTMLDivElement).style.background = 'var(--color-surface-muted, #f3f4f6)' }}
                onMouseLeave={e => { if (!isCurrent) (e.currentTarget as HTMLDivElement).style.background = 'transparent' }}
                >
                  <div style={{
                    width: 40,
                    height: 40,
                    borderRadius: 12,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: app.iconColor,
                    color: '#fff',
                    flexShrink: 0,
                  }}>
                    <Icon size={18} strokeWidth={1.8} />
                  </div>
                  <span style={{
                    fontSize: 10.5,
                    fontWeight: 500,
                    color: 'var(--color-text, #111827)',
                    lineHeight: 1.2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    width: '100%',
                  }}>
                    {app.name}
                  </span>
                </div>
              )

              return (
                <div key={app.id} role="none">
                  {isCurrent ? (
                    <div role="menuitem" aria-current="true">{tile}</div>
                  ) : (
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => app.id === 'admin' ? handleAdminClick() : handleVoiceClick()}
                      style={{
                        display: 'block',
                        width: '100%',
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        cursor: 'pointer',
                        borderRadius: 8,
                        outline: 'none',
                      }}
                      onMouseEnter={(e) => {
                        const el = e.currentTarget.firstElementChild as HTMLElement
                        if (el) el.style.background = 'var(--color-surface-hover, #f3f4f6)'
                      }}
                      onMouseLeave={(e) => {
                        const el = e.currentTarget.firstElementChild as HTMLElement
                        if (el) el.style.background = 'transparent'
                      }}
                    >
                      {tile}
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
